"use client";

import React from "react";
import { m as motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Code2, User } from "lucide-react";
import type { BlogPost } from "@/lib/data/blogs";
import { getInternalBlogLink } from "@/lib/utils/blog-url";

interface EnhancedBlogCardProps {
  post: BlogPost;
  index: number;
  variant?: "default" | "compact" | "featured";
}

export default function EnhancedBlogCard({
  post,
  index,
  variant = "default",
}: EnhancedBlogCardProps) {
  const [imageError, setImageError] = React.useState(false);
  const [authorAvatarError, setAuthorAvatarError] = React.useState(false);
  const containerVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: index * 0.03,
        ease: "easeOut",
      },
    },
  };

  if (variant === "featured") {
    return (
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
        className="w-full mb-12"
      >
        <Link
          href={getInternalBlogLink(`/${post.slug}`)}
          className="group block"
        >
          <article className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center bg-card/5 rounded-3xl p-6 border border-border/40 hover:bg-card/10 transition-all duration-300">
            <div className="relative aspect-video rounded-2xl overflow-hidden bg-muted">
              {post.coverImage && !imageError ? (
                <Image
                  src={post.coverImage}
                  alt={post.title}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                  sizes="(max-width: 768px) 100vw, 600px"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Code2 className="w-8 h-8 text-muted-foreground/20" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-bold tracking-widest uppercase text-primary px-3 py-1 bg-primary/5 rounded-md border border-primary/10">
                  {post.category}
                </span>
                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  Featured
                </span>
              </div>

              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground leading-tight tracking-tight group-hover:text-primary transition-colors">
                {post.title}
              </h2>
              <p className="text-muted-foreground text-sm md:text-base line-clamp-3 leading-relaxed font-medium">
                {post.excerpt}
              </p>

              <div className="flex items-center gap-4 text-[11px] font-bold text-muted-foreground/80">
                <span>
                  {new Date(post.date ?? 0).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
                <span className="opacity-30">•</span>
                <span>{post.readingTime} min read</span>
              </div>
            </div>
          </article>
        </Link>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={containerVariants}
      className="w-full"
    >
      <Link href={getInternalBlogLink(`/${post.slug}`)} className="group block">
        <article className="flex items-start gap-6 py-8 border-b border-border/40 transition-all">
          <div className="flex-1 min-w-0 space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative w-5 h-5 rounded-full overflow-hidden border border-border bg-muted">
                {post.author.avatar && !authorAvatarError ? (
                  <Image
                    src={post.author.avatar}
                    alt={post.author.name}
                    fill
                    className="object-cover grayscale hover:grayscale-0 transition-all"
                    onError={() => setAuthorAvatarError(true)}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-primary/10">
                    <User className="w-3 h-3 text-primary/40" />
                  </div>
                )}
              </div>
              <span className="text-xs font-bold text-foreground">
                {post.author.name}
              </span>
              <span className="text-[10px] text-muted-foreground font-medium">
                {new Date(post.date ?? 0).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl md:text-2xl font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 hidden md:block font-medium opacity-80">
                {post.excerpt}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-3">
                <span className="bg-muted px-2.5 py-1 rounded-md text-[9px] font-bold text-muted-foreground uppercase tracking-wider">
                  {post.category}
                </span>
                <span className="text-[10px] font-bold text-muted-foreground/60">
                  {post.readingTime} min read
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-primary opacity-0 group-hover:opacity-100 transition-all font-bold text-xs uppercase tracking-tighter">
                Read <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>

          <div className="relative w-24 h-24 md:w-36 md:h-28 rounded-xl overflow-hidden bg-muted shrink-0 border border-border/40">
            {post.coverImage && !imageError ? (
              <Image
                src={post.coverImage}
                alt={post.title}
                fill
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                sizes="(max-width: 768px) 96px, 144px"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center opacity-10">
                <Code2 className="w-6 h-6" />
              </div>
            )}
          </div>
        </article>
      </Link>
    </motion.div>
  );
}

